import { ref, computed, onMounted, watch } from "vue";
import L from "leaflet";
import { useStore } from "vuex";
import "leaflet-draw";

export function useCourseOutlinesView() {
  const store = useStore();

  const myMap = ref(null);
  const courses = computed(() => store.getters["courses/getCourseNames"]);
  const outlines = computed(() => store.getters["outlines/getOutlines"]);
  const outlinesEdited = ref(false);
  const associatedCourses = computed(
    () => store.getters["outlines/getAssociatedCourses"]
  );
  const boundsMapAndFlags = computed(
    () => store.getters["holes/getBoundsMapAndFlags"]
  );
  const targetCourse = ref(null);
  const loading = ref(false);
  const showCourseMap = ref(true);
  const isEditing = ref(false);
  const enableSendToDevice = ref(false);

  const zoom = ref(15.5);
  const center = ref([51.505117019117044, -0.15981318574761885]);
  const url = ref(
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
  );
  const opacity = ref(0.6);
  const overlay = ref("");
  const mapImage = ref("");
  const bounds = ref([
    [0, 0],
    [0, 0],
  ]);
  const mapRotation = ref(0);

  const courseOutline = ref(null);
  const drawnItems = ref(null);
  const associatedOutlinesLayer = ref(null); // New layer for associated outlines
  const polygonDrawer = ref(null);

  const ready = (mapInstance) => {
    myMap.value = mapInstance;
    drawnItems.value = new L.FeatureGroup();
    associatedOutlinesLayer.value = new L.FeatureGroup(); // Initialize associated outlines layer
    myMap.value.addLayer(drawnItems.value);
    myMap.value.addLayer(associatedOutlinesLayer.value);
  };

  const changeView = (satellite) => {
    showCourseMap.value = !satellite;
  };

  const startCreatingOutline = () => {
    if (!myMap.value) return;

    if (drawnItems.value) {
      drawnItems.value.clearLayers();
    }

    polygonDrawer.value = new L.Draw.Polygon(myMap.value, {
      allowIntersection: false,
      showArea: true,
      shapeOptions: {
        color: "#3388ff",
        weight: 3,
      },
    });

    polygonDrawer.value.enable();

    isEditing.value = true;

    myMap.value.off(L.Draw.Event.CREATED);
    myMap.value.on(L.Draw.Event.CREATED, onDrawCreated);
  };

  const startEditingOutline = () => {
    if (
      !myMap.value ||
      !drawnItems.value ||
      drawnItems.value.getLayers().length === 0
    )
      return;

    const layer = drawnItems.value.getLayers()[0];

    outlinesEdited.value = true;

    if (layer && layer.editing) {
      layer.editing.enable();
      isEditing.value = true;

      myMap.value.off(L.Draw.Event.EDITED);
      myMap.value.on(L.Draw.Event.EDITED, onDrawEdited);
    }
  };

  const stopEditing = () => {
    if (polygonDrawer.value) {
      polygonDrawer.value.disable();
    }

    drawnItems.value.eachLayer(function (layer) {
      if (layer.editing) {
        layer.editing.disable();
      }
    });

    isEditing.value = false;

    updateCourseOutline();
  };

  const deleteOutline = async () => {
    try {
      loading.value = true;
      await store.dispatch("outlines/deleteOutlines", targetCourse.value.id);

      if (drawnItems.value) {
        drawnItems.value.clearLayers();
      }
      if (associatedOutlinesLayer.value) {
        associatedOutlinesLayer.value.clearLayers();
      }
      courseOutline.value = null;
      loading.value = false;
    } catch (error) {
      loading.value = false;
    }
  };

  const onDrawCreated = (event) => {
    const layer = event.layer;

    if (drawnItems.value) {
      drawnItems.value.clearLayers();
    }

    drawnItems.value.addLayer(layer);
    updateCourseOutline();
  };

  const onDrawEdited = () => {
    updateCourseOutline();
  };

  const updateCourseOutline = () => {
    const layers = drawnItems.value.getLayers();

    if (layers.length > 0) {
      const geoJson = drawnItems.value.toGeoJSON();
      courseOutline.value = geoJson;
    } else {
      courseOutline.value = null;
    }
  };

  const saveCourseOutline = async () => {
    if (!targetCourse.value || !courseOutline.value) {
      store.dispatch("toastNotification/displayToast", {
        message: "No course selected or no outline drawn",
        type: "warning",
      });
      return;
    }

    stopEditing();

    const payload = Array.isArray(courseOutline.value.features)
      ? courseOutline.value.features[0].geometry
      : courseOutline.value.geometry;

    loading.value = true;

    try {
      await store.dispatch("outlines/updateOutlines", {
        id: targetCourse.value.id,
        payload: payload,
      });
      store.commit("outlines/SET_OUTLINES", payload);
      store.dispatch("toastNotification/displayToast", {
        message: "Course outline saved successfully",
        type: "success",
      });

      enableSendToDevice.value = true;

      loading.value = false;
    } catch (error) {
      loading.value = false;
      store.dispatch("toastNotification/displayToast", {
        message: `${
          error.message
            ? Array.isArray(error.message)
              ? error.message.join("<br>")
              : error.message
            : "Error saving course outline"
        }`,
        type: "error",
      });
    }
  };

  const sendConfig = async () => {
    loading.value = true;

    try {
      await store.dispatch("holes/sendConfigToDevices", targetCourse.value.id);
      store.dispatch("toastNotification/displayToast", {
        message: "Configuration sent to devices successfully.",
        type: "success",
      });

      loading.value = false;
    } catch (error) {
      store.dispatch("toastNotification/displayToast", {
        message: `Failed to send configuration \n ${error?.message}`,
        type: "error",
      });
      loading.value = false;
    }
  };

  const loadAssociatedOutlines = () => {
    if (!associatedOutlinesLayer.value || !associatedCourses.value) return;

    associatedOutlinesLayer.value.clearLayers();

    associatedCourses.value.forEach((associatedCourse, index) => {
      if (associatedCourse.associated_outline) {
        try {
          // Parse associated_outline if it's a string
          let outline = associatedCourse.associated_outline;
          let name = associatedCourse.associated_course_name;
          if (typeof outline === "string") {
            outline = JSON.parse(outline);
          }
          if (
            outline &&
            outline.coordinates &&
            Array.isArray(outline.coordinates[0])
          ) {
            const polygon = L.polygon(
              outline.coordinates[0].map((coord) => [coord[1], coord[0]]),
              {
                color: getAssociatedOutlineColor(index),
                weight: 2,
                fillOpacity: 0.1,
                dashArray: "5, 5",
              }
            );

            polygon.bindPopup(`Associate: ${name}`);

            associatedOutlinesLayer.value.addLayer(polygon);
          }
        } catch (error) {
          console.error("Error loading associated outline:", error);
        }
      }
    });
  };

  const getAssociatedOutlineColor = (index) => {
    const colors = ["#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57"];
    return colors[index % colors.length];
  };

  const loadCourseOutline = () => {
    if (!drawnItems.value) return;
    drawnItems.value.clearLayers();

    if (outlines.value) {
      try {
        const polygon = L.polygon(
          outlines.value.coordinates[0].map((coord) => [coord[1], coord[0]]),
          {
            color: "#3388ff",
            weight: 3,
          }
        );

        drawnItems.value.addLayer(polygon);

        courseOutline.value = {
          type: "Feature",
          geometry: outlines.value,
          properties: {},
        };

        myMap.value.fitBounds(polygon.getBounds());
      } catch (error) {
        console.error("Error loading course outline:", error);
      }
    }

    loadAssociatedOutlines();
  };

  const applyBoundsAndOverlays = () => {
    if (!boundsMapAndFlags.value) return;

    const {
      northEastLatitude,
      northEastLongitude,
      southWestLatitude,
      southWestLongitude,
      mapRotation: rotation,
      map,
      flags,
      zoom: mapZoom,
    } = boundsMapAndFlags.value;

    if (
      isValidCoordinate(southWestLatitude) &&
      isValidCoordinate(southWestLongitude) &&
      isValidCoordinate(northEastLatitude) &&
      isValidCoordinate(northEastLongitude)
    ) {
      bounds.value = [
        [southWestLatitude, southWestLongitude],
        [northEastLatitude, northEastLongitude],
      ];

      mapImage.value = map;
      overlay.value = flags;
      mapRotation.value = rotation || 0;

      if (mapZoom) {
        zoom.value = parseFloat(mapZoom);
      }

      const centerLat = (northEastLatitude + southWestLatitude) / 2;
      const centerLng = (northEastLongitude + southWestLongitude) / 2;
      center.value = [centerLat, centerLng];

      setTimeout(() => {
        if (myMap.value) {
          try {
            myMap.value.invalidateSize();
            myMap.value.fitBounds(bounds.value, { animate: false });
          } catch (error) {
            console.error("Error fitting bounds:", error);
          }
        }
      }, 100);
    } else {
      console.warn("Invalid bounds received:", boundsMapAndFlags.value);
    }
  };

  const isValidCoordinate = (coord) => {
    return (
      coord !== null && coord !== undefined && !isNaN(coord) && coord !== 0
    );
  };

  const clearOutlines = () => {
    if (drawnItems.value) {
      drawnItems.value.clearLayers();
    }

    if (associatedOutlinesLayer.value) {
      associatedOutlinesLayer.value.clearLayers();
    }

    courseOutline.value = null;

    center.value = [51.505117019117044, -0.15981318574761885];
    zoom.value = 15.5;

    mapImage.value = "";
    overlay.value = "";

    bounds.value = [
      [0, 0],
      [0, 0],
    ];

    mapRotation.value = 0;

    if (myMap.value) {
      setTimeout(() => {
        myMap.value.setView(center.value, zoom.value, { animate: false });
        myMap.value.invalidateSize();
      }, 100);
    }
  };

  const goToCourse = async (course) => {
    loading.value = true;

    try {
      await store.dispatch("holes/fetchBoundsMapAndFlags", course.id);
      applyBoundsAndOverlays();
    } catch (error) {
      store.dispatch("toastNotification/displayToast", {
        message: `${error?.message || "Error fetching course maps and flags"}`,
        type: "warning",
      });
    }

    try {
      await store.dispatch("outlines/fetchCourseOutlines", course.id);
      loadCourseOutline(); // This will now also load associated outlines
      console.log("Associated Courses:", associatedCourses.value);
      loading.value = false;
    } catch (error) {
      loading.value = false;
      store.dispatch("toastNotification/displayToast", {
        message: `${error?.message || "Error fetching course outlines"}`,
        type: "warning",
      });
      console.error("Error fetching course outlines:", error);
    }
  };

  const hasOutline = computed(() => {
    return drawnItems.value && drawnItems.value.getLayers().length > 0;
  });

  // Watch for changes in associated courses and reload them
  watch(
    () => associatedCourses.value,
    () => {
      loadAssociatedOutlines();
    },
    { deep: true }
  );

  watch(
    () => targetCourse.value,
    async (newVal) => {
      if (newVal) {
        if (isEditing.value) {
          stopEditing();
        }
        goToCourse(newVal);
      } else {
        clearOutlines();
      }
    }
  );

  watch(
    () => boundsMapAndFlags.value,
    (newVal) => {
      if (newVal) {
        applyBoundsAndOverlays();
      }
    }
  );

  onMounted(() => {
    store.dispatch("courses/fetchCourseNames");
  });

  return {
    myMap,
    courses,
    targetCourse,
    loading,
    zoom,
    center,
    url,
    opacity,
    overlay,
    mapImage,
    bounds,
    courseOutline,
    drawnItems,
    showCourseMap,
    mapRotation,
    isEditing,
    hasOutline,
    enableSendToDevice,
    ready,
    changeView,
    startCreatingOutline,
    startEditingOutline,
    outlinesEdited,
    stopEditing,
    deleteOutline,
    saveCourseOutline,
    applyBoundsAndOverlays,
    sendConfig,
  };
}
